<script setup>
import { addThousandsSeparator } from '~/utils/utils.js';
import Card from '~/components/Card.vue';
import InfoTooltip from '~/components/InfoTooltip.vue';
import LoadingSkeleton from '~/components/LoadingSkeleton.vue';
import CardLabel from '~/components/CardLabel.vue';

// Props
const props = defineProps({
    circulatingSupply: { type: Number, default: 0 },
    totalSupply: { type: Number, default: 0 },
    maxSupply: { type: Number, default: 0 },
    loading: { type: Boolean, default: false }
});
</script>

<template>
    <div class="grid grid-cols-1 md:grid-cols-3 cards-gap">
        <!-- Circulating Supply -->
        <Card>
            <div class="flex items-center space-x-4">
                <div class="p-3 bg-green-50 dark:bg-green-900/50 rounded-xl">
                    <svg class="w-6 h-6 text-green-500 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                </div>
                <div>
                    <div class="flex items-center">
                        <CardLabel text="Circulating Supply" />
                        <InfoTooltip text="Total number of KAS tokens in circulation" />
                    </div>
                    <div class="mt-1">
                        <LoadingSkeleton v-if="props.loading" height="28px" width="140px" />
                        <p v-else class="text-xl font-bold text-gray-900 dark:text-white slide-in-up">
                            {{ addThousandsSeparator(props.circulatingSupply, 0) }}
                        </p>
                    </div>
                </div>
            </div>
        </Card>

        <!-- Total Supply -->
        <Card>
            <div class="flex items-center space-x-4">
                <div class="p-3 bg-blue-50 dark:bg-blue-900/50 rounded-xl">
                    <svg class="w-6 h-6 text-blue-500 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                </div>
                <div>
                    <div class="flex items-center">
                        <CardLabel text="Total Supply" />
                        <InfoTooltip text="Total number of KAS tokens that have been created" />
                    </div>
                    <div class="mt-1">
                        <LoadingSkeleton v-if="props.loading" height="28px" width="140px" />
                        <p v-else class="text-xl font-bold text-gray-900 dark:text-white slide-in-up">
                            {{ addThousandsSeparator(props.totalSupply, 0) }}
                        </p>
                    </div>
                </div>
            </div>
        </Card>

        <!-- Max Supply -->
        <Card>
            <div class="flex items-center space-x-4">
                <div class="p-3 bg-purple-50 dark:bg-purple-900/50 rounded-xl">
                    <svg class="w-6 h-6 text-purple-500 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                </div>
                <div>
                    <div class="flex items-center">
                        <CardLabel text="Max Supply" />
                        <InfoTooltip text="Maximum number of KAS tokens that will ever be created" />
                    </div>
                    <div class="mt-1">
                        <LoadingSkeleton v-if="props.loading" height="28px" width="140px" />
                        <p v-else class="text-xl font-bold text-gray-900 dark:text-white slide-in-up">
                            {{ addThousandsSeparator(props.maxSupply, 0) }}
                        </p>
                    </div>
                </div>
            </div>
        </Card>
    </div>
</template>